import { useEventLogging } from '@innovamat/event-logging';
import { stages } from '@innovamat/radiance-utils';
import { useEffect, useState } from 'react';
import type { FullScreenHandle } from 'react-full-screen';

type Props = {
  courseOrder: number | null | undefined;
  handleFS?: FullScreenHandle;
  initialLayout?: string;
  isEnabled?: boolean;
  source?: 'weeklyPractice' | 'trainingZone' | 'catchUpZone';
  onExcessiveFocusLoss?: () => void;
};

type Result = {
  blurCount: number;
  showMissingFocusModal: boolean;
  handleCloseMissingFocusModal: () => void;
  handleLayoutChange: (layout: string) => void;
};

export const useMissingFocusModal = ({
  courseOrder,
  handleFS,
  initialLayout,
  isEnabled,
  source,
  onExcessiveFocusLoss,
}: Props): Result => {
  const { setEventData } = useEventLogging();
  const isSecundaria = !!stages.isSecundaria(courseOrder);

  const [blurCount, setBlurCount] = useState(0);
  const [layout, setLayout] = useState(initialLayout);
  const [showMissingFocusModal, setShowMissingFocusModal] = useState(false);

  const handleCloseMissingFocusModal = (): void => {
    setShowMissingFocusModal(false);

    if (blurCount > 1 && onExcessiveFocusLoss) {
      onExcessiveFocusLoss();
      return;
    }

    if (blurCount === 1 && handleFS) {
      handleFS.enter().catch(() => {
        console.warn('Failed to enter fullscreen mode');
        setEventData('fullscreen_error', {
          error: 'Failed to enter fullscreen mode',
          layout_type: layout,
          source,
        });
      });
    }
  };

  const handleLayoutChange = (newLayout: string): void => {
    setLayout(newLayout);
  };

  useEffect(() => {
    const handleFullscreenChange = (): void => {
      if (!isEnabled || source !== 'weeklyPractice' || !isSecundaria) {
        return;
      }

      if (layout === 'INTERACTIVE_GRAPHIC') {
        return;
      }

      if (!document.fullscreenElement && !showMissingFocusModal) {
        setBlurCount((prev) => prev + 1);
        setShowMissingFocusModal(true);
        setEventData('missing_focus_modal_shown', {
          blur_count: blurCount + 1,
          modal_type: blurCount === 0 ? 'first_warning' : 'final_warning',
          layout_type: layout,
          source,
        });
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [
    blurCount,
    isEnabled,
    isSecundaria,
    layout,
    showMissingFocusModal,
    source,
    setEventData,
  ]);

  return {
    blurCount,
    showMissingFocusModal,
    handleCloseMissingFocusModal,
    handleLayoutChange,
  };
};
